package fr.manyverse.app.domain.validation

import fr.manyverse.app.domain.error.ValidationError

/**
 * Validator for digicode challenge configuration.
 */
object DigicodeValidator {

    const val MIN_DIGIT_COUNT = 3
    const val MAX_DIGIT_COUNT = 8
    const val MIN_ATTEMPTS = 1
    const val MAX_ATTEMPTS = 5
    const val MIN_TIME_LIMIT_SECONDS = 30
    const val MAX_TIME_LIMIT_SECONDS = 300

    fun validateDigitCount(digitCount: Int): Result<Unit> {
        if (digitCount !in MIN_DIGIT_COUNT..MAX_DIGIT_COUNT) {
            return Result.failure(
                ValidationError("Digit count must be between $MIN_DIGIT_COUNT and $MAX_DIGIT_COUNT")
            )
        }
        return Result.success(Unit)
    }

    fun validateMaxAttempts(maxAttempts: Int): Result<Unit> {
        if (maxAttempts !in MIN_ATTEMPTS..MAX_ATTEMPTS) {
            return Result.failure(
                ValidationError("Max attempts must be between $MIN_ATTEMPTS and $MAX_ATTEMPTS")
            )
        }
        return Result.success(Unit)
    }

    fun validateTimeLimit(timeLimitSeconds: Int?): Result<Unit> {
        if (timeLimitSeconds != null) {
            if (timeLimitSeconds !in MIN_TIME_LIMIT_SECONDS..MAX_TIME_LIMIT_SECONDS) {
                return Result.failure(
                    ValidationError(
                        "Time limit must be between ${MIN_TIME_LIMIT_SECONDS}s and ${MAX_TIME_LIMIT_SECONDS}s"
                    )
                )
            }
        }
        return Result.success(Unit)
    }

    fun validateSolution(solution: String, digitCount: Int): Result<Unit> {
        if (solution.isBlank()) {
            return Result.failure(ValidationError("Solution cannot be empty"))
        }
        if (!solution.all { it.isDigit() }) {
            return Result.failure(ValidationError("Solution must contain digits only"))
        }
        if (solution.length != digitCount) {
            return Result.failure(ValidationError("Solution must have exactly $digitCount digits"))
        }
        return Result.success(Unit)
    }

    fun validateTargetParticipant(targetParticipantId: String): Result<Unit> {
        if (targetParticipantId.isBlank()) {
            return Result.failure(ValidationError("A target player must be selected"))
        }
        return Result.success(Unit)
    }

    fun validateForCreation(
        targetParticipantId: String,
        digitCount: Int,
        maxAttempts: Int,
        timeLimitSeconds: Int?,
        solution: String
    ): Result<Unit> {
        validateTargetParticipant(targetParticipantId).onFailure { return Result.failure(it) }
        validateDigitCount(digitCount).onFailure { return Result.failure(it) }
        validateMaxAttempts(maxAttempts).onFailure { return Result.failure(it) }
        validateTimeLimit(timeLimitSeconds).onFailure { return Result.failure(it) }
        validateSolution(solution, digitCount).onFailure { return Result.failure(it) }
        return Result.success(Unit)
    }
}
